import {
  DELIVERABLES,
  type DeliverableKey,
  type StatusFilter,
} from "@/features/properties/services/deliveryStatusConfig";

export type DeliverableFilterState = "any" | "has" | "missing";

export interface PropertyFilters {
  /** Per-status tri-state: 'has' = include that status, 'missing' = exclude it. */
  stages: Partial<Record<StatusFilter, DeliverableFilterState>>;
  deliverables: Partial<Record<DeliverableKey, DeliverableFilterState>>;
  teamIds: string[];
}

export type SortColumn = "name" | "address" | "assetCount";

export interface PropertySort {
  column: SortColumn;
  ascending: boolean;
}

export const EMPTY_FILTERS: PropertyFilters = {
  stages: {},
  deliverables: {},
  teamIds: [],
};

export const DEFAULT_SORT: PropertySort = { column: "name", ascending: true };

/** Maps the column the UI sorts on to the underlying Supabase order target. */
export const SORT_COLUMN_DB: Record<SortColumn, string> = {
  name: "name",
  address: "address",
  assetCount: "property_assets(count)",
};

export interface DeliverableConstraint {
  column: DeliverableKey;
  value: boolean;
}

/** Pure: turn the deliverable filter map into eq constraints. 'any' yields nothing. */
export function buildDeliverableConstraints(
  deliverables: PropertyFilters["deliverables"]
): DeliverableConstraint[] {
  return DELIVERABLES.flatMap((key): DeliverableConstraint[] => {
    const state = deliverables[key];
    if (state === "has") return [{ column: key, value: true }];
    if (state === "missing") return [{ column: key, value: false }];
    return [];
  });
}

/** True when no filter is active — lets the section skip filter wiring. */
export function hasActiveFilters(filters: PropertyFilters): boolean {
  return (
    Object.values(filters.stages).some((s) => s && s !== "any") ||
    filters.teamIds.length > 0 ||
    buildDeliverableConstraints(filters.deliverables).length > 0
  );
}
